import { wrapText } from "@/lib/tool-kit/charts/text";
import {
  type Kind,
  type PedigreePerson,
  type PedigreeUnit,
  type Relation,
  toRoman,
} from "@/lib/tool-kit/ecology/pedigree";

/**
 * Pedigree layout in a fixed coordinate system.
 *
 * Each person owns a slot SLOT wide. A couple takes two slots side by side with the
 * male on the left where there is a choice (Bennett et al. 2008: "male partner should
 * be to left of female partner"), and their children hang from the middle of the
 * relationship line. A unit is as wide as the wider of its couple and its children's
 * units, so nothing overlaps. Rows are one generation each; a row is as tall as its
 * longest label stack.
 */

export const SLOT = 100;
export const SYMBOL = 36;
export const GUTTER = 56;
const MARGIN = 20;
const TOP = 34;
const LABEL_WIDTH = 86;
export const LINE_HEIGHT = 13;
export const LABEL_FONT = 11;
/** Gap under the symbol before the first label line. */
export const LABEL_GAP = 9;
/** From the last label to the sibship line, and from it to the next symbol. */
const BELOW_LABELS = 12;
const DROP = 38;

/** Half the height of a symbol: where the line of descent meets it. */
export function halfHeight(person: PedigreePerson): number {
  if (person.kind === "pregnancy") {
    return 16;
  }
  if (person.kind === "miscarriage" || person.kind === "termination") {
    return 15;
  }
  return person.sex === "u" && person.kind === "person" ? 22 : SYMBOL / 2;
}

export interface PlacedPerson {
  person: PedigreePerson;
  x: number;
  y: number;
  generation: number;
  /** Pedigree number such as II-3. */
  number: string;
  /** Label lines under the symbol, without the pedigree number. */
  lines: string[];
}

export interface PlacedChild {
  placed: PlacedPerson;
  /** The y of the top of its symbol. */
  topY: number;
}

export interface PlacedFamily {
  left: PlacedPerson | null;
  right: PlacedPerson | null;
  relation: Relation;
  /** Middle of the relationship line, where the line of descent leaves it. */
  midX: number;
  y: number;
  sibshipY: number;
  children: PlacedChild[];
}

export interface UsedFeatures {
  sexes: Set<string>;
  statuses: Set<string>;
  kinds: Set<Kind>;
  relations: Set<Relation>;
  deceased: boolean;
  proband: boolean;
  consultand: boolean;
  adopted: boolean;
  mz: boolean;
  dz: boolean;
}

export interface PedigreeLayout {
  people: PlacedPerson[];
  families: PlacedFamily[];
  width: number;
  /** Height without a legend. */
  height: number;
  generations: number;
  /** The y of the centre of each generation's symbols. */
  rows: number[];
  used: UsedFeatures;
}

export interface LayoutOptions {
  showNames: boolean;
  /** Short labels for pregnancy symbols, such as SAB; keyed by kind. */
  abbreviations: Partial<Record<Kind, string>>;
}

function labelLines(person: PedigreePerson, options: LayoutOptions): string[] {
  const lines: string[] = [];
  const abbreviation = options.abbreviations[person.kind];
  if (abbreviation && person.kind !== "pregnancy") {
    lines.push(abbreviation);
  }
  const style = { fontSize: LABEL_FONT };
  if (options.showNames && person.name) {
    lines.push(...wrapText(person.name, LABEL_WIDTH, style, 2));
  }
  const dates = [
    person.age ? `${person.age} y` : "",
    person.born ? `b. ${person.born}` : "",
    person.died ? `d. ${person.died}` : "",
  ].filter(Boolean);
  if (dates.length > 0) {
    lines.push(...wrapText(dates.join(" "), LABEL_WIDTH, style, 2));
  }
  for (const note of person.notes) {
    lines.push(...wrapText(note, LABEL_WIDTH, style, 2));
  }
  return lines;
}

function isCoupleUnit(unit: PedigreeUnit): boolean {
  return unit.partner !== null || unit.children.length > 0;
}

function leftThenRight(
  unit: PedigreeUnit
): [PedigreePerson, PedigreePerson | null] {
  const { person, partner } = unit;
  if (partner && person.sex === "f" && partner.sex === "m") {
    return [partner, person];
  }
  return [person, partner];
}

class Builder {
  readonly people: PlacedPerson[] = [];
  readonly families: PlacedFamily[] = [];
  private readonly widths = new Map<PedigreeUnit, number>();
  private readonly options: LayoutOptions;

  constructor(options: LayoutOptions) {
    this.options = options;
  }

  width(unit: PedigreeUnit): number {
    const known = this.widths.get(unit);
    if (known !== undefined) {
      return known;
    }
    const couple = isCoupleUnit(unit) ? 2 * SLOT : SLOT;
    let kids = 0;
    for (const child of unit.children) {
      kids += this.width(child);
    }
    const value = Math.max(couple, kids);
    this.widths.set(unit, value);
    return value;
  }

  private add(person: PedigreePerson, x: number, generation: number) {
    const placed: PlacedPerson = {
      person,
      x,
      y: 0,
      generation,
      number: "",
      lines: labelLines(person, this.options),
    };
    this.people.push(placed);
    return placed;
  }

  /** Place a unit and its descendants; returns the person who descends from above. */
  place(unit: PedigreeUnit, left: number, generation: number): PlacedPerson {
    const total = this.width(unit);
    const centre = left + total / 2;
    const [a, b] = leftThenRight(unit);
    const single = !isCoupleUnit(unit);
    const aPlaced = this.add(
      a,
      single ? centre : centre - SLOT / 2,
      generation
    );
    const bPlaced = b ? this.add(b, centre + SLOT / 2, generation) : null;
    const family: PlacedFamily = {
      left: aPlaced,
      right: bPlaced,
      relation: unit.relation,
      midX: centre,
      y: 0,
      sibshipY: 0,
      children: [],
    };
    if (!single) {
      this.families.push(family);
    }
    let kidsWidth = 0;
    for (const child of unit.children) {
      kidsWidth += this.width(child);
    }
    let cursor = left + (total - kidsWidth) / 2;
    const kids: PlacedPerson[] = [];
    for (const child of unit.children) {
      kids.push(this.place(child, cursor, generation + 1));
      cursor += this.width(child);
    }
    family.children = kids.map((placed) => ({ placed, topY: 0 }));
    return unit.person === a || !bPlaced ? aPlaced : bPlaced;
  }
}

function emptyUsed(): UsedFeatures {
  return {
    sexes: new Set(),
    statuses: new Set(),
    kinds: new Set(),
    relations: new Set(),
    deceased: false,
    proband: false,
    consultand: false,
    adopted: false,
    mz: false,
    dz: false,
  };
}

function noteUse(used: UsedFeatures, person: PedigreePerson) {
  if (person.kind === "person") {
    used.sexes.add(person.sex);
  } else {
    used.kinds.add(person.kind);
  }
  used.statuses.add(person.status);
  used.deceased ||= person.deceased;
  used.proband ||= person.proband;
  used.consultand ||= person.consultand;
  used.adopted ||= person.adopted;
  used.mz ||= person.twin === "mz";
  used.dz ||= person.twin === "dz";
}

export function layoutPedigree(
  roots: readonly PedigreeUnit[],
  options: LayoutOptions
): PedigreeLayout {
  const builder = new Builder(options);
  let cursor = GUTTER;
  for (const root of roots) {
    builder.place(root, cursor, 0);
    cursor += builder.width(root);
  }
  const { people, families } = builder;
  const generations = Math.max(0, ...people.map((p) => p.generation)) + 1;
  const labelHeights = Array.from({ length: generations }, (_, g) =>
    Math.max(
      0,
      ...people
        .filter((p) => p.generation === g)
        .map((p) => (p.lines.length + 1) * LINE_HEIGHT)
    )
  );
  const rows: number[] = [];
  let y = TOP + SYMBOL / 2;
  for (let g = 0; g < generations; g++) {
    rows.push(y);
    y +=
      SYMBOL / 2 +
      LABEL_GAP +
      labelHeights[g] +
      BELOW_LABELS +
      DROP +
      SYMBOL / 2;
  }
  const used = emptyUsed();
  for (const placed of people) {
    placed.y = rows[placed.generation];
    noteUse(used, placed.person);
  }
  for (const family of families) {
    family.y = rows[family.left?.generation ?? 0];
    used.relations.add(family.relation);
    family.sibshipY =
      family.y +
      SYMBOL / 2 +
      LABEL_GAP +
      labelHeights[family.left?.generation ?? 0] +
      BELOW_LABELS;
    for (const child of family.children) {
      child.topY = child.placed.y - halfHeight(child.placed.person);
    }
  }
  for (let g = 0; g < generations; g++) {
    const inRow = people
      .filter((p) => p.generation === g)
      .sort((a, b) => a.x - b.x);
    for (const [index, placed] of inRow.entries()) {
      placed.number = `${toRoman(g + 1)}-${index + 1}`;
    }
  }
  const lastRow = rows.at(-1) ?? TOP;
  const height =
    lastRow + SYMBOL / 2 + LABEL_GAP + (labelHeights.at(-1) ?? 0) + MARGIN;
  return {
    people,
    families,
    width: Math.max(cursor + MARGIN, 360),
    height,
    generations,
    rows,
    used,
  };
}
